import type { PaginatedResponse } from '@/types/api';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';
import type {
    CompetencyProficiencyHistoryResponse,
    EmployeeCompetencyAssignment,
    Evaluation360Entry,
    ManagerAssessmentEntry,
    PerformanceScore,
    PerformanceTrend,
    SelfAssessmentEntry,
    ProficiencyLevel,
    SaveCompetencyProficiencyHistoryRequest,
    SkillItem,
    SkillsetAssessment,
    SkillsetAssessmentFilters,
    PerformanceScoreWindow,
    SkillsetEvaluator,
} from '@/types/performance-profile';

import { api } from '@/api/api';
import { PREDEFINED_EVALUATION_PERIOD_NAMES } from '@/api/cycle-evaluation-services/manager-assessment/managerAssessmentService';
import { getUtcOffsetMinutes } from '@/utils/dateUtils';
import { getLocalizedText } from '@/utils/localization';

/** One period as the server reports it: an id and the two calendar days that place it. */
interface EvaluationPeriodResponse {
    id: string;
    startDate: string;
    endDate: string;
    isCurrent: boolean;
}

export interface ProficiencyLevelResponse {
    id: string;
    label: Record<string, string>;
    icon: string;
    iconUrl?: string;
    iconColor?: string;
    sortOrder: number;
}

interface RawSkillsetAssessment {
    employeeId: string;
    categories: Array<{
        id: string;
        name: Record<string, string> | string;
        proficiency?: {
            id: string;
            label: Record<string, string>;
            icon: string;
            iconUrl?: string;
            iconColor?: string;
            sortOrder: number;
        };
        skills: Array<{
            id: string;
            name: Record<string, string> | string;
            proficiency?: {
                id: string;
                label: Record<string, string>;
                icon: string;
                iconUrl?: string;
                iconColor?: string;
                sortOrder: number;
            };
            cycleInstanceId: string;
            changeHistory: SkillItem['changeHistory'];
            isRemoved?: boolean; // Indicates if competency is removed/excluded for this employee (Bug 13976)
            isDeactivated?: boolean; // Catalog inactive — keep visible for history
            isDeleted?: boolean; // Soft-deleted catalog — keep visible for history
        }>;
    }>;
}

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

// Treat empty and nil-UUID ids as missing so they are never used as React keys.
const normalizeId = (id: string | undefined, fallback: string) =>
    id && id !== NIL_UUID ? id : fallback;

// Filters out competencies that have been individually removed for this employee (Bug 13976).
// Deactivated catalog competencies are kept so assessment history remains reachable.
const mapSkillsetAssessment = (raw: RawSkillsetAssessment): SkillsetAssessment => {
    return {
        employeeId: raw.employeeId,
        categories: raw.categories
            .map((category, categoryIndex) => ({
                id: normalizeId(category.id, `category-${categoryIndex}`),
                name: getLocalizedText(category.name),
                proficiency: category.proficiency ?? null,
                skills: category.skills
                    .filter((skill) => !skill.isRemoved)
                    .map((skill, skillIndex) => ({
                        id: normalizeId(skill.id, `skill-${categoryIndex}-${skillIndex}`),
                        name: getLocalizedText(skill.name),
                        proficiency: skill.proficiency ?? null,
                        cycleInstanceId: skill.cycleInstanceId,
                        changeHistory: skill.changeHistory ?? [],
                        isDeactivated: skill.isDeactivated === true,
                        isDeleted: skill.isDeleted === true,
                    })),
            }))
            .filter((category) => category.skills.length > 0), // Remove empty categories
    };
};

/**
 * The two routes below bound their reading by calendar days the reader picked, and an evaluation
 * carries an instant: without the reader's offset the server reads those days in Coordinated
 * Universal Time, and an assessment submitted on the evening of a period's last day falls outside
 * it. The score and the competency list have to send the same offset or they disagree about what
 * that last day contains.
 */
export default {
    async getPerformanceScore(
        employeeId: string,
        window?: PerformanceScoreWindow,
    ): Promise<PerformanceScore> {
        const response = await api.get(`/performance-profile/${employeeId}/score`, {
            params: { ...window, utcOffsetMinutes: getUtcOffsetMinutes() },
        });
        return response.data;
    },
    async getPerformanceTrend(employeeId: string): Promise<PerformanceTrend> {
        const response = await api.get(`/performance-profile/${employeeId}/trend`);
        return response.data;
    },
    /**
     * The periods this employee was actually evaluated over, which is what the profile filters by.
     * A period the server does not name here holds nothing, and offering it would empty the page
     * without saying why.
     *
     * Predefined periods keep the name they are chosen under, so the same period reads the same
     * whether it is being set on a cycle or read back off one. A period the setup vocabulary does
     * not know keeps its id here, which is what the two ends match each other on;
     * `useGetEmployeeEvaluationPeriods` is where it becomes words.
     */
    async getEmployeeEvaluationPeriods(employeeId: string): Promise<EvaluationPeriod[]> {
        const response = await api.get<{ periods: EvaluationPeriodResponse[] }>(
            `/performance-profile/${employeeId}/evaluation-periods`,
        );

        return (response.data.periods ?? []).map((period) => ({
            id: period.id,
            name: PREDEFINED_EVALUATION_PERIOD_NAMES[period.id] ?? period.id,
            startDate: period.startDate,
            endDate: period.endDate,
            isCurrent: period.isCurrent,
        }));
    },
    async getEmployeeCompetencyAssignment(
        employeeId: string,
    ): Promise<EmployeeCompetencyAssignment> {
        const response = await api.get(`/performance-profile/${employeeId}/competency-assignment`);
        return response.data;
    },
    async addAdditionalCompetencies(
        employeeId: string,
        competencyIds: string[],
    ): Promise<EmployeeCompetencyAssignment> {
        const response = await api.post(
            `/performance-profile/${employeeId}/competency-assignment/additional-competencies`,
            { competencyIds },
        );
        return response.data;
    },
    async removeAdditionalCompetency(
        employeeId: string,
        competencyId: string,
    ): Promise<EmployeeCompetencyAssignment> {
        const response = await api.delete(
            `/performance-profile/${employeeId}/competency-assignment/additional-competencies/${competencyId}`,
        );
        return response.data;
    },
    async addExcludedCompetency(
        employeeId: string,
        competencyId: string,
    ): Promise<EmployeeCompetencyAssignment> {
        const response = await api.post(
            `/performance-profile/${employeeId}/competency-assignment/excluded-competencies/${competencyId}`,
        );
        return response.data;
    },
    async removeExcludedCompetency(
        employeeId: string,
        competencyId: string,
    ): Promise<EmployeeCompetencyAssignment> {
        const response = await api.delete(
            `/performance-profile/${employeeId}/competency-assignment/excluded-competencies/${competencyId}`,
        );
        return response.data;
    },
    async getSelfAssessments(employeeId: string): Promise<SelfAssessmentEntry[]> {
        const response = await api.get(`/performance-profile/${employeeId}/self-assessments`);
        return response.data;
    },
    async getEvaluation360(employeeId: string): Promise<Evaluation360Entry[]> {
        const response = await api.get(`/performance-profile/${employeeId}/evaluation-360`);
        return response.data;
    },
    async getManagerAssessments(employeeId: string): Promise<ManagerAssessmentEntry[]> {
        const response = await api.get(`/performance-profile/${employeeId}/manager-assessments`);
        return response.data;
    },
    async getSkillsetAssessment(
        employeeId: string,
        { filterId, fromDate, toDate, evaluatorId, competencySet }: SkillsetAssessmentFilters = {},
    ): Promise<SkillsetAssessment> {
        const params: Record<string, string | number> = {
            utcOffsetMinutes: getUtcOffsetMinutes(),
        };
        if (filterId && filterId !== 'all-profiles') {
            params.filterId = filterId;
        }
        if (fromDate) {
            params.fromDate = fromDate;
        }
        if (toDate) {
            params.toDate = toDate;
        }
        if (evaluatorId) {
            params.evaluatorId = evaluatorId;
        }
        if (competencySet) {
            params.competencySet = competencySet;
        }
        const response = await api.get(`/performance-profile/${employeeId}/skillset-assessment`, {
            params: params,
        });
        return mapSkillsetAssessment(response.data);
    },
    async getSkillsetEvaluators(employeeId: string): Promise<SkillsetEvaluator[]> {
        const response = await api.get(
            `/performance-profile/${employeeId}/skillset-assessment/evaluators`,
        );
        return response.data;
    },
    async updateSkillProficiency(
        employeeId: string,
        skillId: string,
        proficiency: ProficiencyLevel,
        comment: string,
        cycleInstanceId: string,
    ): Promise<void> {
        await api.post('/performance-profile/competency/history', {
            employeeId,
            cycleInstanceId: cycleInstanceId === NIL_UUID ? undefined : cycleInstanceId,
            competencyId: skillId,
            proficiencyLevelId: proficiency?.id,
            comment: comment || undefined,
        });
    },
    async getAssessmentHistory(
        employeeId: string,
        skillId: string,
        pageNumber: number = 1,
        pageSize: number = 30,
    ): Promise<PaginatedResponse<CompetencyProficiencyHistoryResponse>> {
        const response = await api.get(
            `/performance-profile/${employeeId}/competency/${skillId}/history`,
            { params: { pageNumber, pageSize } },
        );
        return response.data;
    },
    async getProficiencyLevels(): Promise<ProficiencyLevelResponse[]> {
        const response = await api.get('/proficiencylevel');
        return response.data;
    },
    async getSkillProficiency(
        cycleInstanceId: string,
        competencyId: string,
    ): Promise<string | null> {
        const response = await api.get(
            `/performance-profile/${cycleInstanceId}/skill/${competencyId}/proficiency`,
        );
        return response.data;
    },
    async getCompetencyProficiencyHistory(
        employeeId: string,
        competencyId: string,
        pageNumber: number = 1,
        pageSize: number = 30,
    ): Promise<PaginatedResponse<CompetencyProficiencyHistoryResponse>> {
        const response = await api.get(
            `/performance-profile/${employeeId}/competency/${competencyId}/history`,
            { params: { pageNumber, pageSize } },
        );
        return response.data;
    },
    async saveCompetencyProficiencyHistory(
        request: SaveCompetencyProficiencyHistoryRequest,
    ): Promise<void> {
        await api.post('/performance-profile/competency/history', request);
    },
};
